/**
 * Stone card live figures.
 *
 * Fills the "Blocks" and "Available (m²)" cells of collection stone cards
 * (snippets/stone-product-card.liquid) from the FindMyMarble stone-types
 * endpoint -- ONE request per page, matched by data-stone-name.
 *
 * Fails silent: without the API, on error, or when a stone has no record,
 * the two cells stay hidden and the card keeps its server-rendered content.
 * Nothing is estimated or invented.
 */
(function () {
  'use strict';

  var apiUrl = window.__FINDMYMARBLE_API_URL;
  if (!apiUrl || typeof fetch !== 'function') return;
  apiUrl = String(apiUrl).replace(/\/+$/, '');

  var TIMEOUT_MS = 8000;
  var loading = null;
  var areaFormat = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 });

  function normalize(name) {
    return name ? String(name).trim().toLowerCase() : '';
  }

  function loadIndex() {
    if (loading) return loading;
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, TIMEOUT_MS) : null;

    loading = fetch(apiUrl + '/api/shopify/stone-types', controller ? { signal: controller.signal } : undefined)
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (data) {
        var index = {};
        if (!data || !Array.isArray(data.stone_types)) throw new Error('Invalid stock response');
        var list = data.stone_types;
        for (var i = 0; i < list.length; i++) {
          var key = normalize(list[i].stone_name);
          if (key) index[key] = list[i];
        }
        return index;
      })
      .catch(function () {
        return null;
      })
      .then(function (index) {
        if (timer) clearTimeout(timer);
        return index;
      });

    return loading;
  }

  function reveal(cell) {
    var wrapper = cell.closest('[data-stone-card-cell]');
    if (wrapper) wrapper.hidden = false;
  }

  function fillCard(card, index) {
    if (card.getAttribute('data-stone-card-live') === 'done') return;
    card.setAttribute('data-stone-card-live', 'done');

    // STATIC products (owner directive 2026-09-03) are not fed from the live
    // stone inventory, so this script says nothing about them at all: no live
    // figures, no fading, no badge change. Marked in Liquid by
    // snippets/stone-product-card.liquid via snippets/stone-is-static.liquid.
    if (card.hasAttribute('data-stone-card-static')) return;

    if (!index) return;
    var name = normalize(card.getAttribute('data-stone-name'));
    if (!name) return;
    var record = index[name];
    if (!record) {
      // The index contains available stones only. Preserve a known zero;
      // missing positive/unknown records no longer support an Available claim.
      if (card.getAttribute('data-stock-state') !== 'unavailable') {
        card.setAttribute('data-stock-state', 'enquire');
        card.classList.remove('is-out-of-stock');
        var badge = card.querySelector('[data-stone-card-status]');
        if (badge) {
          badge.textContent = 'Enquire';
          badge.classList.remove('stone-card__status--oos');
          badge.classList.add('stone-card__status--enquire');
        }
        var stock = card.querySelector('[data-stone-card-stock]');
        if (stock) stock.textContent = 'Enquire';
        var cells = card.querySelectorAll('[data-stone-card-cell]');
        for (var j = 0; j < cells.length; j++) cells[j].hidden = true;
      }
      return;
    }
    if (record.block_count === null || record.block_count === undefined || record.block_count === '') return;

    var blocks = Number(record.block_count);
    var sqm = record.total_available_sqm == null || record.total_available_sqm === '' ? NaN : Number(record.total_available_sqm);
    if (!isFinite(blocks) || blocks < 0 || Math.floor(blocks) !== blocks) return;

    var blocksCell = card.querySelector('[data-stone-card-blocks]');
    if (blocksCell && isFinite(blocks) && blocks >= 0) {
      blocksCell.textContent = String(blocks);
      reveal(blocksCell);
    }

    var sqmCell = card.querySelector('[data-stone-card-sqm]');
    if (sqmCell && isFinite(sqm) && sqm >= 0) {
      sqmCell.textContent = areaFormat.format(sqm) + ' m²';
      reveal(sqmCell);
    } else if (sqmCell) {
      sqmCell.textContent = '\u2014';
      var areaWrapper = sqmCell.closest('[data-stone-card-cell]');
      if (areaWrapper) areaWrapper.hidden = true;
    }

    markOutOfStock(card, blocks);
  }

  // A successful source response updates every stock statement together.
  // Missing/malformed responses preserve the server's verified snapshot.
  function markOutOfStock(card, blocks) {
    var unavailable = blocks === 0;
    card.classList.toggle('is-out-of-stock', unavailable);
    card.setAttribute('data-stock-state', unavailable ? 'unavailable' : 'available');
    var status = card.querySelector('[data-stone-card-status]');
    var stock = card.querySelector('[data-stone-card-stock]');
    var label = status && status.getAttribute('data-oos-label') || 'Out of stock';
    if (status) {
      status.textContent = unavailable ? label : 'Available';
      status.classList.remove('stone-card__status--enquire');
      status.classList.toggle('stone-card__status--oos', unavailable);
    }
    if (stock) stock.textContent = unavailable ? label : 'In stock';
    if (unavailable) {
      var sqm = card.querySelector('[data-stone-card-sqm]');
      if (sqm) { sqm.textContent = '0 m²'; reveal(sqm); }
    }
  }

  function run(root) {
    var cards = (root || document).querySelectorAll('[data-stone-card]:not([data-stone-card-live])');
    if (!cards.length) return;
    loadIndex().then(function (index) {
      for (var i = 0; i < cards.length; i++) fillCard(cards[i], index);
    });
  }

  function start() {
    run(document);
    var grid = document.getElementById('product-grid');
    if (grid && typeof MutationObserver === 'function') {
      new MutationObserver(function () { run(grid); }).observe(grid, { childList: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
